import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { z } from 'zod';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { saveAs } from 'file-saver';
import { Icon } from '@/components/Icon';
import { Button } from '@/components/Button';
import { Badge, type BadgeVariant } from '@/components/Badge';
import { Select, SelectOption } from '@/components/Select';
import { ListPanel } from '@/components/ListPanel';
import { PageHeader } from '@/components/PageHeader';
import { Table, type TableColumn, type TableAction } from '@/components/Table';
import { StatCard } from '@/components/StatCard';
import { Tabs, type TabItem } from '@/components/Tabs';
import { Tooltip } from '@/components/Tooltip';
import { Loading } from '@/components/Loading';
import { useToast } from '@/context/ToastContext';
import { clientApi, tenderApi } from '@/lib/api';
import { Client, Tender, TenderKpis } from '@/pages/Tenders/types';
import { TenderFormModal } from '@/pages/Tenders/TenderFormModal';
import { TenderDetailDrawer } from '@/pages/Tenders/TenderDetailDrawer';
import { TenderStatusModal } from '@/pages/Tenders/TenderStatusModal';
import { TenderStudyModal } from '@/pages/Tenders/TenderStudyModal';
import { ModalDelete } from '@/components/ModalDelete';
import { ListSettings } from '@/components/ListSettings';
import { SavedViews } from '@/components/SavedViews';
import { listPreferencesSchema, listSortSchema, listViewSchema, type ListPreferences } from '@/components/listPreferences';
import { useStoredPreference } from '@/hooks/useStoredPreference';
import { useServerList, useDebouncedSearch } from '@/hooks/useServerList';
import { useRecordDialog } from '@/hooks/useRecordDialog';
import { useClientLookup } from '@/hooks/useClientLookup';

type MainTab = 'all' | 'active' | 'historical';

const VALID_MAIN_TABS: MainTab[] = ['all', 'active', 'historical'];

export const TendersList: React.FC = () => {
    const { t } = useTranslation('global');
    const { showSuccess, showError } = useToast();
    const navigate = useNavigate();
    const { mainTab: rawMainTab, subTab: rawSubTab } = useParams<{ mainTab?: string; subTab?: string }>();

    const mainTab: MainTab = (VALID_MAIN_TABS.includes(rawMainTab as MainTab) ? rawMainTab : 'all') as MainTab;
    const subTab: string = rawSubTab || 'all';
    const statusTab = mainTab === 'all'
        ? (subTab === 'historical' ? 'in_study' : subTab)
        : mainTab === 'active'
            ? (subTab === 'decision_pending' || subTab === 'study_pending' ? subTab : 'all')
            : (subTab === 'rejected' ? 'rejected' : subTab === 'in_study' ? 'in_study' : 'all');

    // Data state
    const [kpis, setKpis] = useState<TenderKpis>({
        total: 0,
        in_study: 0,
        active_total: 0,
        total_budget: 0,
        decision_pending: 0,
        study_pending: 0,
        accepted: 0,
        rejected: 0,
        historical: 0,
    });

    // Filters
    const [search, setSearch] = useStoredPreference('vigepra_tenders_search', z.string().max(500), '', true);
    const [clientFilter, setClientFilter] = useStoredPreference('vigepra_tenders_client', z.string(), 'all', true);
    const [yearFilter, setYearFilter] = useStoredPreference('vigepra_tenders_year', z.string(), 'all', true);
    const clientLookup = useClientLookup(clientFilter);
    const clients = clientLookup.items;
    const [filtersOpen, setFiltersOpen] = useState<boolean>(false);
    const [preferences, setPreferences] = useStoredPreference<ListPreferences>('vigepra_tenders_table', listPreferencesSchema, { density: 'comfortable', hidden: [] });
    const [sort, setSort] = useStoredPreference<z.infer<typeof listSortSchema>>('vigepra_tenders_sort', listSortSchema, { key: 'submission_date', order: 'desc' });
    const delayedSearch = useDebouncedSearch(search);
    const statusMap: Record<string, number> = { decision_pending: 1, study_pending: 2, accepted: 3, rejected: 4, historical: 5 };
    const listing = useServerList<Tender>('tenders', '/tenders', { tab: statusTab === 'in_study' ? 'in_study' : mainTab, status: String(statusMap[statusTab] || 'all'),
        search: delayedSearch, client_id: clientFilter, year: yearFilter, sort_by: sort.key, sort_order: sort.order });
    const loading = listing.loading;
    const filteredTenders = listing.items;

    // Modals state
    const record = useRecordDialog<Tender>('tenders');
    const selectedTender = record.item;
    const setSelectedTender = record.select;
    const [formModalOpen, setFormModalOpen] = useState<boolean>(false);
    const [editingTender, setEditingTender] = useState<Tender | null>(null);
    const [statusModalOpen, setStatusModalOpen] = useState<boolean>(false);
    const [statusTargetTender, setStatusTargetTender] = useState<Tender | null>(null);
    const [studyModalOpen, setStudyModalOpen] = useState<boolean>(false);
    const [studyTargetTender, setStudyTargetTender] = useState<Tender | null>(null);
    const [studyIsApproveMode, setStudyIsApproveMode] = useState<boolean>(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
    const [deleteTargetTender, setDeleteTargetTender] = useState<Tender | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState<boolean>(false);

    const handleStatusTabChange = (tab: string): void => {
        navigate(tab === 'all' ? '/tenders/all' : `/tenders/all/${tab}`);
    };

    const loadCommonData = useCallback(async (): Promise<void> => {
        try {
            const kpisRes = await tenderApi.kpis();

            if(kpisRes?.data){
                setKpis(kpisRes.data);
            }

        } catch (error) {
            console.error(error);
        }
    }, []);

    const loadData = async (): Promise<void> => { await loadCommonData(); listing.reload(); };
    useEffect(() => { void loadCommonData(); }, [loadCommonData]);

    const handleOpenCreate = (): void => {
        setEditingTender(null);
        setFormModalOpen(true);
    };

    const handleOpenEdit = (tender: Tender): void => {
        setEditingTender(tender);
        setFormModalOpen(true);
    };

    const handleApproveTender = async (tender: Tender): Promise<void> => {
        if(tender.status_id === 1){
            try {
                const res = await tenderApi.changeStatus(tender.id, 2, t('tender.action_accept_tender'));
                if(res?.data){
                    showSuccess(t('tender.status_changed_success'));
                    await loadData();
                }
            } catch (error) {
                console.error(error);
                showError(error instanceof Error ? error.message : t('common.error'));
            }
        }
        else{
            if(tender.status_id === 2){
                setStudyTargetTender(tender);
                setStudyIsApproveMode(true);
                setStudyModalOpen(true);
            }
        }
    };

    const handleOpenStatus = (tender: Tender): void => {
        setStatusTargetTender(tender);
        setStatusModalOpen(true);
    };

    const handleOpenDelete = (tender: Tender): void => {
        setDeleteTargetTender(tender);
        setDeleteModalOpen(true);
    };

    const handleDownloadBc3 = async (tender: Tender): Promise<void> => {
        if(!tender.bc3_file_path){
            return;
        }

        try {
            const blob = await tenderApi.downloadBc3(tender.id);
            const fileName = tender.bc3_file_name || `${tender.code}.bc3`;
            saveAs(blob, fileName);
            showSuccess(t('developments.detail.task_downloaded'));
        } catch (error) {
            console.error(error);
            showError(error instanceof Error ? error.message : t('common.error'));
        }
    };

    const handleFormSuccess = async (saved: Tender): Promise<void> => {
        setFormModalOpen(false);
        setEditingTender(null);
        showSuccess(editingTender ? t('tender.updated_success') : t('tender.created_success'));
        if(selectedTender && selectedTender.id === saved.id){
            setSelectedTender(saved);
        }
        await loadData();
    };

    const handleStatusSuccess = async (newStatus: import('@/pages/Tenders/types').TenderStatus, reason: string | null): Promise<void> => {
        if(!statusTargetTender){
            return;
        }

        setIsUpdatingStatus(true);
        try {
            const statusIds = { decision_pending: 1, study_pending: 2, accepted: 3, rejected: 4, historical: 5 };
            const res = await tenderApi.changeStatus(statusTargetTender.id, statusIds[newStatus], reason);
            if(res?.data){
                showSuccess(t('tender.status_changed_success'));
                setStatusModalOpen(false);
                setStatusTargetTender(null);
                if(selectedTender && selectedTender.id === statusTargetTender.id){
                    setSelectedTender(res.data);
                }
                await loadData();
            }
        } catch (error) {
            console.error(error);
            showError(error instanceof Error ? error.message : t('common.error'));
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handleConfirmDelete = async (): Promise<void> => {
        if(!deleteTargetTender){
            return;
        }

        setIsDeleting(true);
        try {
            await tenderApi.delete(deleteTargetTender.id);
            showSuccess(t('tender.deleted_success'));
            setDeleteModalOpen(false);
            setDeleteTargetTender(null);
            if(selectedTender && selectedTender.id === deleteTargetTender.id){
                setSelectedTender(null);
            }
            await loadData();
        } catch (error) {
            console.error(error);
            showError(error instanceof Error ? error.message : t('common.error'));
        } finally {
            setIsDeleting(false);
        }
    };

    // Client options for filter
    const clientOptions: SelectOption<string>[] = useMemo(() => {
        return [
            { value: 'all', label: t('tender.filter_all_clients') },
            ...clients.map((c: Client) => ({
                value: String(c.id),
                label: c.name,
            })),
        ];
    }, [clients, t]);

    const statusTabItems: TabItem<string>[] = useMemo(() => [
        {
            id: 'all',
            label: t('tender.tab_all'),
            badge: mainTab === 'active'
                ? (kpis.decision_pending || 0) + (kpis.study_pending || 0)
                : mainTab === 'historical'
                    ? (kpis.rejected || 0) + (kpis.historical || 0)
                    : kpis.total,
        },
        {
            id: 'decision_pending',
            label: t('tender.status.decision_pending_plural'),
            badge: kpis.decision_pending            
        },
        {
            id: 'study_pending',
            label: t('tender.status.study_pending_plural'),
            badge: kpis.study_pending
        },
        {
            id: 'accepted',
            label: t('tender.status.accepted_label_plural'),
            badge: kpis.accepted,
        },
        {
            id: 'rejected',
            label: t('tender.status.rejected_plural'),
            badge: kpis.rejected,
        },
        {
            id: 'in_study',
            label: t('tender.tab_in_study'),
            badge: kpis.in_study ?? 0,
        },
    ], [kpis, mainTab, t]);

    const tableColumns: TableColumn<Tender>[] = [
        {
            key: 'code',
            label: t('tender.col_code'),
            sortable: true,
            nowrap: true,
            render: (tender: Tender) => (
                <Badge
                    value={tender.code}
                />
            ),
        },
        {
            key: 'submission_date',
            label: t('tender.col_submission_date'),
            type: 'date',
            sortable: true,
            align: 'center',
            nowrap: true,
        },
        {
            key: 'title',
            label: t('tender.col_work'),
            sortable: true,
            render: (tender: Tender) => {
                const prov = typeof tender.province === 'object' && tender.province !== null
                    ? tender.province.name
                    : (tender.province || '');
                return (
                    <div className="flex flex-col py-0.5 min-w-[220px]">
                        <span className="font-semibold text-slate-900 dark:text-white">
                            {tender.title}
                        </span>
                        <span className="text-[12px] text-slate-500 dark:text-zinc-400 line-clamp-1">
                            {[tender.city, prov].filter(Boolean).join(', ')}
                        </span>
                    </div>
                );
            },
        },
        {
            key: 'work_type_id',
            label: t('tender.col_work_type'),
            sortable: true,
            nowrap: true,
            render: (tender: Tender) => {
                const workTypeName = tender.project_type?.name || tender.work_type?.name;
                if(!workTypeName){
                    return <span className="text-slate-400">—</span>;
                }
                const color = tender.project_type?.color || tender.work_type?.color || 'success';
                const icon = tender.project_type?.icon || tender.work_type?.icon;

                return (
                    <Badge
                        color={color}
                        icon={icon || undefined}
                        value={workTypeName}
                    />
                );
            },
        },
        {
            key: 'client_id',
            label: t('tender.col_client'),
            sortable: true,
            render: (tender: Tender) => {
                const clientName = tender.client?.name;
                if (!clientName) return <span className="text-slate-400">—</span>;
                return (
                    <Tooltip title={clientName} placement="top">
                        <span className="flex flex-col text-slate-700 dark:text-zinc-300 line-clamp-1 max-w-[200px]">
                            {clientName}
                        </span>
                    </Tooltip>
                );
            }
        },
        {
            key: 'client_type_id',
            label: t('tender.col_client_type'),
            sortable: true,
            nowrap: true,
            render: (tender: Tender) => {
                const clientType = tender.client?.client_type || tender.client_type;
                const clientTypeName = clientType?.name;
                if(!clientTypeName){
                    return <span className="text-slate-400">—</span>;
                }
                const isPublic = clientTypeName.toLowerCase().includes('públic') || clientTypeName.toLowerCase().includes('public');
                const isCivil = clientTypeName.toLowerCase().includes('civil');
                const defaultFallback = isPublic ? 'success' : (isCivil ? 'neutral' : 'warning');
                const defaultIcon = isPublic ? 'landmark' : (isCivil ? 'hardHat' : 'house');

                const color = clientType?.color || defaultFallback;
                const icon = clientType?.icon || defaultIcon;

                return (
                    <Badge
                        color={color}
                        icon={icon}
                        value={clientTypeName}
                    />
                );
            },
        },        
        {
            key: 'deadline_period',
            label: t('tender.col_deadline'),
            sortable: true,
            nowrap: true,
            render: (tender: Tender) => {
                if(!tender.deadline_period){
                    return <span className="text-slate-600 dark:text-zinc-400">—</span>;
                }

                const rawUnit = tender.deadline_unit as any;
                const unit = typeof rawUnit === 'string' ? rawUnit : (rawUnit?.tag || 'days');
                const baseUnit = unit.endsWith('s') ? unit.slice(0, -1) : unit;
                const unitKey = tender.deadline_period === 1 ? `tender.unit_${baseUnit}` : `tender.unit_${baseUnit}s`;

                return (
                    <span className="text-slate-600 dark:text-zinc-400">
                        {`${tender.deadline_period} ${t(unitKey)}`}
                    </span>
                );
            },
        },
        {
            key: 'budget',
            label: t('tender.col_budget'),
            type: 'price',
            sortable: true,
            align: 'right',
            nowrap: true,
        },
        {
            key: 'valuation_criteria',
            label: t('tender.col_criteria'),
            sortable: true,
            render: (tender: Tender) => (
                <span className="text-slate-600 dark:text-zinc-400 text-xs line-clamp-1 max-w-[180px]" title={tender.valuation_criteria || ''}>
                    {tender.valuation_criteria || '—'}
                </span>
            ),
        },
        {
            key: 'status',
            label: t('tender.col_status'),
            sortable: true,
            render: (tender: Tender) => {
                const sId = tender.status_id;
                const statusMap: Record<number, { labelKey: string; variant: BadgeVariant }> = {
                    1: { labelKey: 'tender.status.decision_pending', variant: 'warning' },
                    2: { labelKey: 'tender.status.study_pending', variant: 'info' },
                    3: { labelKey: 'tender.status.accepted_label', variant: 'success' },
                    4: { labelKey: 'tender.status.rejected', variant: 'danger' },
                    5: { labelKey: 'tender.status.in_study', variant: 'info' },
                    6: { labelKey: 'tender.status.submitted', variant: 'info' },
                    7: { labelKey: 'tender.status.not_submitted', variant: 'neutral' },
                    8: { labelKey: 'tender.status.awarded', variant: 'success' },
                    9: { labelKey: 'tender.status.not_awarded', variant: 'neutral' },
                };
                const info = statusMap[sId] ?? { labelKey: 'tender.status.decision_pending', variant: 'neutral' };
                return (
                    <Badge
                        variant={info.variant}
                        value={t(info.labelKey)}
                        className="cursor-pointer hover:opacity-80 transition-opacity"
                        onClick={(e) => {
                            e.stopPropagation();
                            if(tender.status_id === 2){
                                setStudyTargetTender(tender);
                                setStudyIsApproveMode(true);
                                setStudyModalOpen(true);
                            }
                            else{
                                handleOpenStatus(tender);
                            }
                        }}
                    />
                );
            },
        },
    ];

    const tableActions: TableAction<Tender>[] = [
        {
            label: t('tender.action_view_detail'),
            icon: <Icon value="eye" size={14} className="text-slate-600 dark:text-zinc-300" />,
            onClick: (row: Tender): void => {
                setSelectedTender(row);
            },
        },
        {
            label: t('tender.action_accept_tender'),
            icon: <Icon value="check" size={14} className="text-emerald-600 dark:text-emerald-400" />,
            show: (row: Tender) => row.status_id === 1,
            onClick: (row: Tender): void => {
                void handleApproveTender(row);
            },
        },
        {
            label: t('tender.action_send_to_study'),
            icon: <Icon value="arrowRight" size={14} className="text-primary" />,
            show: (row: Tender) => row.status_id === 2,
            onClick: (row: Tender): void => {
                void handleApproveTender(row);
            },
        },
        {
            label: t('tender.action_edit'),
            icon: <Icon value="edit" size={14} className="text-slate-600 dark:text-zinc-300" />,
            onClick: (row: Tender): void => {
                handleOpenEdit(row);
            },
        },
        {
            label: t('tender.action_change_status'),
            icon: <Icon value="refresh" size={14} className="text-slate-600 dark:text-zinc-300" />,
            onClick: (row: Tender): void => {
                handleOpenStatus(row);
            },
        },
        {
            label: t('tender.action_download_bc3'),
            icon: <Icon value="download" size={14} className="text-slate-600 dark:text-zinc-300" />,
            show: (row: Tender) => !!row.bc3_file_path,
            onClick: (row: Tender): void => {
                void handleDownloadBc3(row);
            },
        },
        {
            label: t('table.archive'),
            icon: <Icon value="archive" size={14} className="text-rose-600" />,
            variant: 'delete',
            onClick: (row: Tender): void => {
                handleOpenDelete(row);
            },
        },
    ];

    // Year options for filter
    const yearOptions: SelectOption<string>[] = useMemo(() => {
        const years: SelectOption<string>[] = [
            { value: 'all', label: t('tender.filter_all_years') },
        ];
        for(const year of listing.metadata?.years || []){ years.push({ value: year, label: year }); }
        return years;
    }, [t, listing.metadata?.years]);

    const filterElement = (
        <div className="flex flex-wrap items-center gap-1.5">
            <Button variant="outline" size="sm" icon="filter" aria-expanded={filtersOpen} onClick={() => setFiltersOpen(previous => !previous)}>
                {t('providers.ui.filters')}
            </Button>
            <SavedViews
                storageKey="vigepra_tender_views"
                schema={listViewSchema}
                defaultValue={{ search: '', filters: { client: 'all', year: 'all' }, sort: { key: 'submission_date', order: 'desc' }, preferences: { density: 'comfortable', hidden: [] }, pageSize: 20, context: 'all/all' }}
                configuration={<ListSettings inline columns={tableColumns} value={preferences} onChange={setPreferences} sort={sort} onSortChange={setSort} />}
                value={{ search, filters: { client: clientFilter, year: yearFilter }, sort, preferences, pageSize: listing.pagination.pageSize, context: `${mainTab}/${subTab}` }}
                onApply={view => {
                    setSearch(view.search);
                    setClientFilter(view.filters.client || 'all');
                    setYearFilter(view.filters.year || 'all');
                    setPreferences(view.preferences);
                    setSort(view.sort);
                    listing.pagination.onPageSize(view.pageSize);
                    if(/^(all|active|historical)\/(all|decision_pending|study_pending|accepted|rejected|historical|in_study)$/.test(view.context)){ navigate(`/tenders/${view.context}`); }
                }}
            />
        </div>
    );

    return (
        <div className="page min-h-[400px] space-y-3">
            {loading && <Loading />}
            {/* Page Header */}
            <PageHeader
                compact
                className="!mb-0"
                title={t('tender.title')}
                action={{
                    label: t('tender.new_tender'),
                    icon: 'plus',
                    onClick: handleOpenCreate,
                }}
            />

            {/* KPI Cards — Sin tile de presupuesto total */}
            {/*
                mainTab === 'active' ?                    
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4 mb-4">
                        <StatCard
                            title={t('tender.kpi_active')}
                            value={(kpis.decision_pending || 0) + (kpis.study_pending || 0)}
                            icon="briefcase"
                            colorClass="text-blue-600 dark:text-blue-400"
                            valueColorClass="text-slate-900 dark:text-white"
                            onClick={() => handleMainTabChange('active')}
                        />

                        <StatCard
                            title={t('tender.kpi_decision')}
                            value={kpis.decision_pending}
                            icon="bell"
                            colorClass="text-amber-600 dark:text-amber-400"
                            valueColorClass="text-amber-600 dark:text-amber-400"
                            onClick={() => navigate('/tenders/active/decision_pending')}
                        />

                        <StatCard
                            title={t('tender.kpi_study')}
                            value={kpis.study_pending}
                            icon="search"
                            colorClass="text-indigo-600 dark:text-indigo-400"
                            valueColorClass="text-indigo-600 dark:text-indigo-400"
                            onClick={() => navigate('/tenders/active/study_pending')}
                        />
                    </div>                     
                :
                
                    <div className="grid grid-cols-2 gap-4 mt-4 mb-4">
                        <StatCard
                            title={t('tender.tab_in_study')}
                            value={kpis.historical || 0}
                            icon="folder"
                            colorClass="text-slate-600 dark:text-slate-400"
                            valueColorClass="text-slate-900 dark:text-white"
                            onClick={() => navigate('/tenders/historical/in_study')}
                        />

                        <StatCard
                            title={t('tender.kpi_rejected')}
                            value={kpis.rejected || 0}
                            icon="trash"
                            colorClass="text-rose-600 dark:text-rose-400"
                            valueColorClass="text-rose-600 dark:text-rose-400"
                            onClick={() => navigate('/tenders/historical/rejected')}
                        />
                    </div>
            */}

            <Tabs<string>
                tabs={statusTabItems}
                activeTab={statusTab}
                onChange={handleStatusTabChange}
                variant="underline"
                full={false}
                scroll={true}
                syncUrl={false}
                className="mb-3"
            />

            {/* Table */}
            <Table
                type="simple"
                appearance="workspace"
                data={filteredTenders}
                sort={sort}
                onSortChange={setSort}
                manualSorting
                pagination={listing.pagination}
                error={listing.error || record.error}
                onRetry={() => { listing.reload(); record.reload(); }}
                density={preferences.density}
                columns={tableColumns.filter(column => !preferences.hidden.includes(column.key))}
                actions={tableActions}
                actionsPosition="right"
                actionMenu={true}
                searchable={true}
                filterbox={false}
                searchValue={search}
                onSearchChange={(val: string) => setSearch(val)}
                searchPlaceholder={t('tender.search_placeholder')}
                filterElement={filterElement}
                filterFooter={
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                        {clientFilter !== 'all' && <Button variant="outline" size="xs" icon="x" onClick={() => setClientFilter('all')}>{clients.find(client => String(client.id) === clientFilter)?.name}</Button>}
                        {yearFilter !== 'all' && <Button variant="outline" size="xs" icon="x" onClick={() => setYearFilter('all')}>{yearFilter}</Button>}
                        {(clientFilter !== 'all' || yearFilter !== 'all' || search) && <Button variant="ghost" size="xs" onClick={() => { setSearch(''); setClientFilter('all'); setYearFilter('all'); }}>{t('workspace.list.clear')}</Button>}
                    </div>
                }
                loading={loading}
                emptyText={search || clientFilter !== 'all' || yearFilter !== 'all' ? t('tender.empty_title_filtered') : t('tender.empty_title')}
                paginated={true}
                defaultPageSize={10}
                defaultSortKey="submission_date"
                defaultSortOrder="desc"
                onRowClick={(row: any) => setSelectedTender(row as Tender)}
                filterPanel={<ListPanel isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} title={t('providers.ui.filters')}
                    footer={<Button size="sm" variant="ghost" onClick={() => { setClientFilter('all'); setYearFilter('all'); }}>{t('workspace.list.clear')}</Button>}>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                        <Select<string> label={t('tender.col_client')} value={clientFilter} onChange={value => setClientFilter(value || 'all')} options={clientOptions} onSearchChange={clientLookup.search} searchable clearable />
                        <Select<string> label={t('workspace.list.year')} value={yearFilter} onChange={value => setYearFilter(value || 'all')} options={yearOptions} />
                    </div>
                </ListPanel>}
            />

            {/* Modals & Detail Modal */}
            <TenderFormModal
                isOpen={formModalOpen}
                onClose={() => {
                    setFormModalOpen(false);
                    setEditingTender(null);
                }}
                tender={editingTender}
                onSuccess={handleFormSuccess}
            />

            <TenderDetailDrawer
                tender={selectedTender}
                onClose={() => setSelectedTender(null)}
                onEdit={(tnd) => {
                    setSelectedTender(null);
                    handleOpenEdit(tnd);
                }}
                onChangeStatus={(tnd) => {
                    setSelectedTender(null);
                    handleOpenStatus(tnd);
                }}
                onDelete={(tnd) => {
                    setSelectedTender(null);
                    handleOpenDelete(tnd);
                }}
                onRefresh={async () => {
                    await loadData();
                    if(selectedTender){
                        const res = await tenderApi.get(selectedTender.id);
                        if(res?.data){
                            setSelectedTender(res.data);
                        }
                    }
                }}
            />

            <TenderStatusModal
                isOpen={statusModalOpen}
                onClose={() => {
                    setStatusModalOpen(false);
                    setStatusTargetTender(null);
                }}
                tender={statusTargetTender}
                isSubmitting={isUpdatingStatus}
                onSubmit={handleStatusSuccess}
            />

            <TenderStudyModal
                isOpen={studyModalOpen}
                tender={studyTargetTender}
                isApproveMode={studyIsApproveMode}
                onClose={() => {
                    setStudyModalOpen(false);
                    setStudyTargetTender(null);
                }}
                onSuccess={async (saved) => {
                    if(selectedTender && selectedTender.id === saved.id){
                        setSelectedTender(saved);
                    }
                    await loadData();
                }}
            />

            <ModalDelete
                isOpen={deleteModalOpen}
                onClose={() => {
                    setDeleteModalOpen(false);
                    setDeleteTargetTender(null);
                }}
                //title={t('tender.delete_confirm_title')}
                description={t('tender.delete_confirm_desc')}
                itemName={deleteTargetTender?.code || ''}
                itemDescription={deleteTargetTender?.title}
                isDeleting={isDeleting}
                onConfirm={handleConfirmDelete}
                submitLabel={t('common.archive')}
            />
        </div>
    );
};

export default TendersList;
